import React, { useState } from "react"
import styled, { css } from "styled-components"
import compose from "recompose/compose"
import themeGet from "@styled-system/theme-get"
import { withi18n } from "@/helpers/hoc/withi18n"
import { withTracking } from "@/helpers/hoc/withTracking"
import { Text } from "@xingternal/winery-ui/lib/Text"
import { Dropdown } from "@xingternal/winery-ui/lib/Dropdown"
import { SearchInput, Tooltip } from "@xingternal/360-components"
import { useBenchmarkSettingsContext } from "@/components/BenchmarkSettingsContext"
import { useIsRebrandingEnabled } from "@xingternal/rebranding-adapter"
import { RadioButton } from "@xingternal/winery-ui/lib/RadioButton"

const i18n = {
  title: "360_RECRUITER_INSIGHTS_BENCHMARKS_PICKER_TOP_RECRUITERS",
  corporate: "360_RECRUITER_INSIGHTS_BENCHMARKS_PICKER_CORPORATE_RECRUITERS",
  headhunter: "360_RECRUITER_INSIGHTS_BENCHMARKS_PICKER_HEADHUNTERS",
  notEnoughData: "360_RECRUITER_INSIGHTS_BENCHMARKS_PICKER_NO_DATA",
}

const DROPDOWN_WIDTH = "260px"

const oldFontSize = css`
  font-size: 13px;
`

const TextCustom = styled(Text)`
  ${oldFontSize}
`

const DropdownMenuWrapper = styled(Dropdown.Menu)`
  padding-top: 0;
  width: ${DROPDOWN_WIDTH};
  max-height: 250px;
`

const DropdownTriggerWrapper = styled(Dropdown.Trigger)`
  ${oldFontSize}

  width: ${DROPDOWN_WIDTH};
  margin-top: ${themeGet("space.2")};
`

const Title = styled(TextCustom).attrs({ bold: true })``

const RadioButtonStyled = styled(RadioButton)`
  margin-top: ${themeGet("space.1")};
  margin-left: ${themeGet("space.1")};
`

const RadioButtonInputStyled = styled(RadioButton.Input)`
  &&:checked + div {
    border: 2px solid
      ${({ enableXingRebranding }) =>
        enableXingRebranding
          ? themeGet("colors.grey800")
          : themeGet("colors.lightPetrol")};
    &::after {
      background: ${({ enableXingRebranding }) =>
        enableXingRebranding
          ? themeGet("colors.grey800")
          : themeGet("colors.lightPetrol")};
    }
  }
`

const RecruiterTypeSelectorWrapper = styled.div`
  margin-bottom: ${themeGet("space.4")};
`
const DropdownMenuItemStyled = styled(Dropdown.MenuItem)`
  &[aria-selected="true"] {
    background: ${({ enableXingRebranding }) =>
      enableXingRebranding
        ? themeGet("colors.grey800")
        : themeGet("colors.lightPetrol")};
  }
`

const enhance = compose(withi18n(i18n), withTracking())

const RecruiterTypeSelector = enhance(
  ({ corporate, headhunter, trackAction }) => {
    const { benchmarkSettings, setRecruiterType, recruiterTypes } =
      useBenchmarkSettingsContext()
    const { enableXingRebranding } = useIsRebrandingEnabled()
    const onSetRecruiterType = (recruiterType) => () => {
      const sufixForRecruiterType = {
        corporate: "corporate_recruiter",
        headhunter: "headhunter",
      }
      setRecruiterType(recruiterType)
      trackAction(
        `xri_date_picker_${sufixForRecruiterType[recruiterType]}_select`,
      )
    }

    return (
      <RecruiterTypeSelectorWrapper>
        <RadioButtonStyled>
          <RadioButtonInputStyled
            id={recruiterTypes.corporate}
            value={recruiterTypes.corporate}
            label={corporate}
            checked={
              benchmarkSettings.recruiterType === recruiterTypes.corporate
            }
            onChange={onSetRecruiterType(recruiterTypes.corporate)}
            enableXingRebranding={enableXingRebranding}
          />
          <RadioButtonInputStyled
            id={recruiterTypes.headhunter}
            value={recruiterTypes.headhunter}
            label={headhunter}
            checked={
              benchmarkSettings.recruiterType === recruiterTypes.headhunter
            }
            onChange={onSetRecruiterType(recruiterTypes.headhunter)}
            enableXingRebranding={enableXingRebranding}
          />
        </RadioButtonStyled>
      </RecruiterTypeSelectorWrapper>
    )
  },
)

const IndustryItem = withi18n()(({ t, active, industry, onClick, ...rest }) => {
  const { enableXingRebranding } = useIsRebrandingEnabled()

  return (
    <DropdownMenuItemStyled
      key={industry.id}
      active={active}
      onClick={onClick}
      style={{ fontSize: 13 }}
      {...rest}
      enableXingRebranding={enableXingRebranding}
    >
      {t(industry.translationKey)}
    </DropdownMenuItemStyled>
  )
})

const IndustryFilterWrapper = styled.div`
  background: white;
  position: sticky;
  top: 0;
  padding: ${themeGet("space.3")};
  border-bottom: 1px solid ${themeGet("colors.borderMedium")};
`

const SearchInputWrapper = styled(SearchInput)`
  width: 100%;

  svg {
    vertical-align: sub;
  }
`

const DefaultItem = styled.div`
  ${oldFontSize}

  padding-bottom: ${themeGet("space.2")};
  margin-top: ${themeGet("space.2")};
  margin-bottom: ${themeGet("space.2")};
  border-bottom: 1px solid ${themeGet("colors.borderMedium")};

  & div {
    width: 100%;
  }
`

const IndustrySelectorList = withi18n()(({
  industries,
  t,
  onIndustryClick,
  isActive,
}) => {
  const [searchText, setSearchText] = useState("")

  const defaultIndustry = industries[0]
  let allIndustries = industries
    .slice(1)
    .filter((industry) =>
      t(industry.translationKey)
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    )

  return (
    <>
      <IndustryFilterWrapper>
        <SearchInputWrapper placeholder="Search" onChange={setSearchText} />
      </IndustryFilterWrapper>
      <DefaultItem>
        <IndustryItem
          industry={defaultIndustry}
          active={isActive(defaultIndustry)}
          onClick={onIndustryClick(defaultIndustry)}
        />
      </DefaultItem>

      {allIndustries.map((industry) => (
        <IndustryItem
          key={industry.id}
          industry={industry}
          active={isActive(industry)}
          onClick={onIndustryClick(industry)}
        />
      ))}
    </>
  )
})

const IndustrySelector = enhance(({ t, onTrackAction }) => {
  const {
    benchmarkSettings: { industry: currentIndustry, recruiterType },
    industries,
    setIndustry,
  } = useBenchmarkSettingsContext()

  const onIndustryClick = (industry) => () => {
    setIndustry(industry)
    onTrackAction("xri_date_picker_industry_select", {
      industry: `xri_industry_${industry.id}`,
      companySize: `xri_employees_all`,
    })
  }

  return (
    <Dropdown>
      <DropdownTriggerWrapper disabled={recruiterType === "headhunter"}>
        {t(currentIndustry.translationKey)}
      </DropdownTriggerWrapper>
      <DropdownMenuWrapper>
        <IndustrySelectorList
          industries={industries}
          onIndustryClick={onIndustryClick}
          isActive={(industry) => industry.id === currentIndustry.id}
        />
      </DropdownMenuWrapper>
    </Dropdown>
  )
})

const DisabledCompanySizeMenuItem = withi18n(i18n)(styled(Tooltip).attrs(
  ({ notEnoughData }) => ({ text: notEnoughData }),
)`
  width: 100%;
  opacity: 0.5;

  button {
    cursor: default !important;
  }
`)

const DisabledCompanySizeMenuItemWrapper = styled.div`
  /* Needed to fix a tooltip misalignment in Safari when the item is disabled */
  position: relative;
`

const CompanySizeItem = withi18n()(({
  t,
  active,
  companySize,
  onClick,
  disabled,
  ...rest
}) => {
  const { enableXingRebranding } = useIsRebrandingEnabled()

  const DropdownMenuItem = (
    <DropdownMenuItemStyled
      key={companySize.id}
      active={active}
      onClick={!disabled ? onClick : () => {}}
      style={{ fontSize: 13 }}
      {...rest}
      enableXingRebranding={enableXingRebranding}
    >
      {t(companySize.translationKey)}
    </DropdownMenuItemStyled>
  )

  return disabled ? (
    <DisabledCompanySizeMenuItemWrapper>
      <DisabledCompanySizeMenuItem>
        {DropdownMenuItem}
      </DisabledCompanySizeMenuItem>
    </DisabledCompanySizeMenuItemWrapper>
  ) : (
    DropdownMenuItem
  )
})

const CompanySizeSelectorList = ({
  companySizes,
  onCompanySizeClick,
  isActive,
}) => (
  <>
    <DefaultItem>
      <CompanySizeItem
        companySize={companySizes[0]}
        active={isActive(companySizes[0])}
        onClick={onCompanySizeClick(companySizes[0])}
      />
    </DefaultItem>
    {companySizes.slice(1).map((companySize) => (
      <CompanySizeItem
        key={companySize.id}
        companySize={companySize}
        active={isActive(companySize)}
        disabled={!companySize.enoughData}
        onClick={onCompanySizeClick(companySize)}
      />
    ))}
  </>
)

const CompanySizeSelector = withi18n(i18n)(({ t, onTrackAction }) => {
  const {
    benchmarkSettings: { companySize: currentCompanySize },
    companySizes,
    setCompanySize,
  } = useBenchmarkSettingsContext()

  const onCompanySizeClick = (companySize) => () => {
    setCompanySize(companySize)
    onTrackAction("xri_date_picker_employees_select", {
      companySize: `xri_employees_${companySize.id}`,
    })
  }

  return (
    <Dropdown>
      <DropdownTriggerWrapper>
        {t(currentCompanySize.translationKey)}
      </DropdownTriggerWrapper>
      <DropdownMenuWrapper>
        <CompanySizeSelectorList
          companySizes={companySizes}
          onCompanySizeClick={onCompanySizeClick}
          isActive={(companySize) => companySize.id === currentCompanySize.id}
        />
      </DropdownMenuWrapper>
    </Dropdown>
  )
})

const BenchmarkSettings = enhance(({ className, title, trackAction }) => {
  const [trackingState, setTrackingState] = useState({})

  const onTrackAction = (propTrackAction, options) => {
    const currentFilters = { ...trackingState, ...options }
    setTrackingState(currentFilters)

    const PropSearchListFilter = Object.values(currentFilters).join(";")

    trackAction(propTrackAction, { PropSearchListFilter })
  }

  return (
    <div className={className}>
      <Title>{title}</Title>
      <RecruiterTypeSelector />
      <IndustrySelector onTrackAction={onTrackAction} />
      <CompanySizeSelector onTrackAction={onTrackAction} />
    </div>
  )
})

export default BenchmarkSettings
